/*
 * This module loads resources from the server, with added features:
 * 
 * - Allows loading "packages" (.zipped files containing groupped assets)
 * - Allows passing a list of preferred extensions
 * - Caches data
 * 
 */

// import { BlobReader, BlobWriter, TextWriter, ZipReader } from '@zip.js/zip.js';
import { devlog, programmingError } from '../util/log';

const MODULE = 'viewer3d::resloader';

/** Possible types of data stored in cache (and loadable) */
type CachedData = string|Blob;
/** Type of data stored */
type CachedType = 'text' | 'binary';
/** Extensions identified as text extensions */
const TEXT_EXTENSIONS = [ 'json', 'gltf' ];

/**
 * Status of the cached data
 */
type Cached = {
                type: 'text' | 'binary' | 'package'
              } & (
                {
                    // data successfully stored
                    status: 'ok',
                    data: CachedData
                    extension: string
                } | {
                    // data is being loaded
                    status: 'loading',
                    promise: Promise<void>
                } | {
                    // data has failed to load
                    status: 'failed'
                }
              );

/**
 * Location of a resource, optionally inside a package.
 * 
 * If the resource is potentially part of a package, the package name is the
 * path of the package file, without `.zip` or the folder. The loader would
 * try first loading it as a package. If it's known for sure that the resource
 * is not inside a package (single files), `pkg` must be `undefined`.
 * 
 * The name of the resource is the path inside the package, without the file
 * extension. The loader will try to load the resource through the extensions
 * in order.
 */
export interface Resource {
    /** Path of the package folder, or zip file (without extension) or `undefined`  */
    pkg?: string
    /** Full path (it there is no package) or path relative to the package, without extenstion */
    name: string
    /** Prefered extensions, by order */
    exts: string[]
}

/**
 * ResourceLoader
 * 
 */
export class ResourceLoader {

    private baseURL: string
    private cache: Map<string,Cached>;

    /**
     * Creates a new resource loader and cache.
     * @param baseURL A base URL used to load all other URLs.
     */
    constructor(baseURL: string) {
        this.baseURL = baseURL;
        this.cache = new Map();
    }

    /**
     * Loads a textual (string) data from the network.
     * 
     * If the `pkg` parameter is `null`, the loaded doesn't even try to load
     * it from a package. Otherwise, it first try lo load it as package and
     * if it fails, fallbacks to loading it directly using the name as a folder.
     * 
     * @param resource Resource location
     * @returns The data and the extension that succeeded.
     */
    async loadAsText(r: Resource): Promise<[string,string]> {
        return this.loadAndCache(r,  r => r.text(), 'text' );
    }

    /**
     * Loads a blob (binary data) from the network.
     * 
     * If the `pkg` parameter is `null`, the loaded doesn't even try to load
     * it from a package. Otherwise, it first try lo load it as package and
     * if it fails, fallbacks to loading it directly using the name as a folder.
     * 
     * @param resource Resource location
     * @returns The data and the extension that succeeded.
     */
    async loadAsBlob(r: Resource): Promise<[Blob,string]> {
        const result = await this.loadAndCache(r, r => r.blob(), 'binary' );
        return result;
    }

    /**
     * Loads a resource, trying to load first as a package, and caches the result.
     * 
     * @param resource Resource location
     * @param extractFn A function that takes an HTTPResponse and returns the data in the format expected
     * @param expectedType What kind of data (text/binary/...) should be stored in a cache
     * @returns The data and the extension that succeeded.
     */
    private async loadAndCache<T extends CachedData>(resource: Resource,
                                                     extractFn: (r:Response) => Promise<T>,
                                                     expectedType: CachedType
                                                    ): Promise<[T, string]> {

        const { pkg, name, exts } = resource;

        if(pkg) {
            await this.loadAndCachePackage(pkg, true);
        }

        const cachedURL = pkg ? `${pkg}/${name}` : name;
        if(this.cache.has(cachedURL)) {
            let cached = this.cache.get(cachedURL)!;
            if(cached.type !== expectedType) {
                throw new Error(`Invalid cached data type for ${cachedURL}, found: ${cached.type} expected ${expectedType}`);
            }
            switch(cached.status) {
                case 'ok':
                    return [ cached.data as T, cached.extension ]
                    break;

                case 'loading':
                    await cached.promise;
                    return this.loadAndCache(resource, extractFn, expectedType);
                    break;

                case 'failed':
                    // Do nothing will fallback to loading the resource
                    break;
            }
        }

        for(let extension of exts) {
            const fullURL = `${cachedURL}.${extension}`;
            const loadingPromise = this.loadPendingFile(fullURL, extractFn);
            const fullP = loadingPromise.then(data => {
                this.cache.set(cachedURL, {
                    status: 'ok',
                    data,
                    extension,
                    type: expectedType
                });
            })
            this.cache.set(cachedURL, {
                status: 'loading',
                promise: loadingPromise.then(d => {}),
                type: expectedType
            });
            try {
                const [ data, ] = await Promise.all([loadingPromise,fullP]);
                return [ data, extension ];

            } catch(err) {
                // Do nothing, try next extension
            }
        }

        // If here, all extensions have failed
        this.cache.set(cachedURL, {
            status: 'failed',
            type: expectedType
        });
        throw new Error(`Failed to load resource ${cachedURL}`);

    }

    /**
     * Loads a package (zipped file) and caches its contents. 
     * 
     * It is for convenience divided between this, that controls the caching
     * logic of the package itself and loadPendingPackage that fetches
     * and caches the content.
     * 
     * Doesn't store itself as 
     * 
     * @param pkg Package path
     * @param allowFail true if failing to load it is not an error
     * @returns Promise fulfilled when the content of the package has been processed.
     */
    private async loadAndCachePackage(pkg: string, allowFail: boolean): Promise<void> {
        if(allowFail) {
            return ;
        } else {
            programmingError(MODULE, 'Error loading package ${pkg}', { pkg });
        }
        /*
        try {
            const pkgURL = `${pkg}.zip`;
            if(!this.cache.has(pkgURL)) {
                const loadingPromise = this.loadPendingPackage(pkg);
                this.cache.set(pkgURL, {
                    status: 'loading',
                    promise: loadingPromise,
                    type: 'package'
                });
                await loadingPromise;
                this.cache.set(pkgURL, {
                    status: 'ok',
                    data: '',
                    extension: '.zip',
                    type: 'package'
                })
            } else {
                const cached = this.cache.get(pkgURL)!;
                if(cached.status === 'ok' || cached.status === 'failed') {
                    return;
                } else if(cached.status === 'loading') {
                    await cached.promise;
                }
            }
        } catch(e) {
            if(allowFail) {
                return ;
            } else {
                programmingError(MODULE, 'Error loading package ${pkg}', { pkg });
            }
        }
        */
    }

    /**
     * Loads a file and extracts its data.
     * 
     * @param url Full URL, with extension, etc.
     * @param extractFn A function that takes an HTTPResponse and returns the data in the format expected
     * @returns The requested data
     */
    private async loadPendingFile<T extends CachedData>(url: string, extractFn: (r:Response) => Promise<T>): Promise<T> {
        const response = await this.fetch(url);
        const data = await extractFn(response);
        return data;
    }


    /**
     * Loads a package, unzips it and stores in the cache its content.
     * 
     * @param pkg Package name
     */
    /*
    private async loadPendingPackage(pkg: string): Promise<void> {
        const pkgURL = `${pkg}.zip`;
        const response = await this.fetch(pkgURL);
        const blob = await response.blob();
        const blobReader = new BlobReader(blob);
        const zipReader = new ZipReader(blobReader);
        const entries = await zipReader.getEntries();
        for(const entry of entries) {
            const fileName = entry.filename;
            const extension = getExtension(fileName);
            const withoutFolder = fileName.substring(fileName.indexOf('/') + 1);
            const normalizedName = withoutFolder.substring(0, withoutFolder.length - extension.length - 1);
            const cacheURL = `${pkg}/${normalizedName}`;
            let data: string|Blob;
            let type: CachedType;
            if(TEXT_EXTENSIONS.includes(extension)) {
                const textWriter = new TextWriter();
                // @ts-ignore
                await entry.getData(textWriter);
                data = await textWriter.getData();
                type = 'text';
            } else {
                const blobWriter = new BlobWriter();
                // @ts-ignore
                await entry.getData(blobWriter);
                data = await blobWriter.getData();
                type = 'binary';
            }
            this.cache.set(cacheURL, {
                status: 'ok',
                data,
                extension,
                type
            });
        }
        await zipReader.close();
    }
    */

    /**
     * Loads the url from the network, using the baseURL.
     * 
     * @param url URL, with path and extension, without the base URL
     * @returns A Response object
     */
    private async fetch(url: string): Promise<Response> {
        let fullURL = urljoin(this.baseURL,url);
        let response = await fetch(fullURL);
        if(!response.ok) {
            throw new Error(`Failed to load ${fullURL}`);
        }
        return response;
    }

}

function getExtension(path: string): string {
    return path.substring( path.lastIndexOf('.') + 1 ).toLowerCase();
}

function urljoin(...segments: string[]): string {
    
    let normalized = segments.map((segment, i) => {
        // TODO calc index, substring
        let n = segment;
        while(n.startsWith('/')) {
            n = n.substring(1);
        }
        while(n.endsWith('/')) {
            n = n.substring(0,n.length - 1);
        }
        return n;
    }).join('/')

    if(segments[0].startsWith('/')) {
        normalized = '/' + normalized;
    }

    return normalized;
}